/**
 * AssetPackManager - On-demand pack loading for Quickit pack system
 *
 * Loads packs when needed, never unloads during run.
 * Source-of-truth is pack manifests, not filename parsing.
 */
import Phaser from 'phaser';

// Types for pack manifests
export interface PackAtlas {
  key: string;
  png: string;
  json: string;
}

export interface PackTileset {
  key: string;
  png: string;
  json?: string;
}

export interface PackMap {
  key: string;
  tmj: string;
}

export interface PackAudio {
  key: string;
  src: string;
  type: 'bgm' | 'sfx' | 'ambient';
  loop: boolean;
  volume: number;
}

export interface PackEntry {
  path: string;
  atlases?: PackAtlas[];
  tilesets?: PackTileset[];
  maps?: PackMap[];
  audio?: PackAudio[];
  dependsOn?: string[];
}

export interface PackIndex {
  version: number;
  buildRevision: string | null;
  lastBuilt: string | null;
  packs: Record<string, PackEntry>;
}

export interface PackManifest {
  packId: string;
  type: 'common' | 'room' | 'subject';
  version: number;
  displayName: string;
  description: string;
  dependsOn: string[];
  audio?: {
    preferredBgm?: string;
    tracks: Array<{
      key: string;
      src: string;
      type: 'bgm' | 'sfx' | 'ambient';
      loop: boolean;
      volume: number;
    }>;
  };
}

type LoadState = 'not-loaded' | 'loading' | 'loaded' | 'error';

interface PackState {
  state: LoadState;
  manifest: PackManifest | null;
  error: string | null;
}

/**
 * Manages pack loading for scenes.
 * Usage:
 *   await AssetPackManager.loadPack(scene, 'SCOTUS_OUTSIDE');
 *   // Dependencies (COMMON) are loaded automatically
 */
export class AssetPackManager {
  private static index: PackIndex | null = null;
  private static packStates: Map<string, PackState> = new Map();
  private static indexLoaded = false;

  /**
   * Load the pack index (call once at game start)
   */
  static async loadIndex(scene: Phaser.Scene): Promise<PackIndex> {
    if (this.index && this.indexLoaded) {
      return this.index;
    }

    return new Promise((resolve, reject) => {
      const key = '__pack_index__';
      
      scene.load.json(key, 'assets/packs/index.json');
      
      scene.load.once('complete', () => {
        this.index = scene.cache.json.get(key) as PackIndex;
        this.indexLoaded = true;
        
        // Initialize pack states
        if (this.index?.packs) {
          for (const packId of Object.keys(this.index.packs)) {
            if (!this.packStates.has(packId)) {
              this.packStates.set(packId, {
                state: 'not-loaded',
                manifest: null,
                error: null,
              });
            }
          }
        }
        
        resolve(this.index);
      });
      
      scene.load.once('loaderror', (file: Phaser.Loader.File) => {
        reject(new Error(`Failed to load pack index: ${file.key}`));
      });
      
      scene.load.start();
    });
  }

  /**
   * Load a pack and all its dependencies
   */
  static async loadPack(scene: Phaser.Scene, packId: string): Promise<void> {
    // Ensure index is loaded
    if (!this.index) {
      await this.loadIndex(scene);
    }

    const packEntry = this.index?.packs[packId];
    if (!packEntry) {
      throw new Error(`Pack not found: ${packId}`);
    }

    const state = this.packStates.get(packId);
    
    // Already loaded or loading
    if (state?.state === 'loaded') {
      return;
    }
    if (state?.state === 'loading') {
      // Wait for existing load
      return this.waitForPack(packId);
    }

    // Load dependencies first
    if (packEntry.dependsOn && packEntry.dependsOn.length > 0) {
      for (const depId of packEntry.dependsOn) {
        await this.loadPack(scene, depId);
      }
    }

    // Mark as loading
    this.packStates.set(packId, {
      state: 'loading',
      manifest: null,
      error: null,
    });

    try {
      // Load pack manifest
      const manifest = await this.loadManifest(scene, packId, packEntry.path);
      
      // Load pack assets
      await this.loadPackAssets(scene, packId, packEntry);
      
      // Mark as loaded
      this.packStates.set(packId, {
        state: 'loaded',
        manifest,
        error: null,
      });
      
      console.log(`✓ Pack loaded: ${packId}`);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      this.packStates.set(packId, {
        state: 'error',
        manifest: null,
        error: errorMsg,
      });
      throw error;
    }
  }

  /**
   * Wait for a pack that's currently loading
   */
  private static waitForPack(packId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const check = () => {
        const state = this.packStates.get(packId);
        if (state?.state === 'loaded') {
          resolve();
        } else if (state?.state === 'error') {
          reject(new Error(state.error || 'Pack load failed'));
        } else {
          setTimeout(check, 50);
        }
      };
      check();
    });
  }

  /**
   * Load pack manifest JSON
   */
  private static loadManifest(
    scene: Phaser.Scene,
    packId: string,
    manifestPath: string
  ): Promise<PackManifest> {
    return new Promise((resolve, reject) => {
      const key = `__manifest_${packId}__`;
      
      // Resolve path relative to packs folder
      const fullPath = `assets/packs/${manifestPath}`;
      
      scene.load.json(key, fullPath);
      
      scene.load.once('complete', () => {
        const manifest = scene.cache.json.get(key) as PackManifest;
        resolve(manifest);
      });
      
      scene.load.once('loaderror', (file: Phaser.Loader.File) => {
        reject(new Error(`Failed to load manifest for ${packId}: ${file.key}`));
      });
      
      scene.load.start();
    });
  }

  /**
   * Load all assets defined in a pack entry
   */
  private static loadPackAssets(
    scene: Phaser.Scene,
    packId: string,
    entry: PackEntry
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      let hasAssets = false;

      // Load atlases
      if (entry.atlases && entry.atlases.length > 0) {
        for (const atlas of entry.atlases) {
          if (!scene.textures.exists(atlas.key)) {
            scene.load.atlas(atlas.key, atlas.png, atlas.json);
            hasAssets = true;
          }
        }
      }

      // Load tilesets
      if (entry.tilesets && entry.tilesets.length > 0) {
        for (const tileset of entry.tilesets) {
          if (!scene.textures.exists(tileset.key)) {
            scene.load.image(tileset.key, tileset.png);
            hasAssets = true;
            // Load tileset JSON if provided
            if (tileset.json) {
              scene.load.json(`${tileset.key}_data`, tileset.json);
            }
          }
        }
      }

      // Load tilemaps
      if (entry.maps && entry.maps.length > 0) {
        for (const map of entry.maps) {
          if (!scene.cache.tilemap.exists(map.key)) {
            scene.load.tilemapTiledJSON(map.key, map.tmj);
            hasAssets = true;
          }
        }
      }

      // Load audio
      if (entry.audio && entry.audio.length > 0) {
        for (const audio of entry.audio) {
          if (!scene.cache.audio.exists(audio.key)) {
            scene.load.audio(audio.key, audio.src);
            hasAssets = true;
          }
        }
      }

      if (!hasAssets) {
        resolve();
        return;
      }

      scene.load.once('complete', () => {
        resolve();
      });

      scene.load.once('loaderror', (file: Phaser.Loader.File) => {
        reject(new Error(`Failed to load asset for ${packId}: ${file.key}`));
      });

      scene.load.start();
    });
  }

  /**
   * Check if a pack is loaded
   */
  static isLoaded(packId: string): boolean {
    return this.packStates.get(packId)?.state === 'loaded';
  }

  /**
   * Get the state of a pack
   */
  static getState(packId: string): LoadState {
    return this.packStates.get(packId)?.state || 'not-loaded';
  }

  /**
   * Get the manifest for a loaded pack
   */
  static getManifest(packId: string): PackManifest | null {
    return this.packStates.get(packId)?.manifest || null;
  }

  /**
   * Get preferred BGM key for a pack
   */
  static getPreferredBgm(packId: string): string | null {
    const manifest = this.getManifest(packId);
    return manifest?.audio?.preferredBgm || null;
  }

  /**
   * Get all loaded pack IDs
   */
  static getLoadedPacks(): string[] {
    const loaded: string[] = [];
    for (const [packId, state] of this.packStates) {
      if (state.state === 'loaded') {
        loaded.push(packId);
      }
    }
    return loaded;
  }

  /**
   * Get pack index (must call loadIndex first)
   */
  static getIndex(): PackIndex | null {
    return this.index;
  }

  /**
   * Get build revision from index
   */
  static getBuildRevision(): string | null {
    return this.index?.buildRevision || null;
  }

  /**
   * Reset manager state (for testing)
   */
  static reset(): void {
    this.index = null;
    this.indexLoaded = false;
    this.packStates.clear();
  }
}
